/* -------------------------- Design imports start -------------------------- */
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

import ChannelOverview from './ChannelOverview'
import Button from '../../../../components/Button'
import DeleteModal from '../../../../components/widgets/DeleteModal'
import LogTool from '../../../../logger/logTools'
import { getPKfromSelf } from '../../../../utils/functions'
import { type User } from '../../../../utils/types'
import { useWebsocket } from '../../../../utils/WebsocketProvider'
import { ChatContext } from '../../utils/context'
import { createChannel, createMessage } from "../../utils/functions";
import { fetchChannel, fetchMessages as fetchMessagesApi, fetchChannels as fetchChannelsApi } from '../../api/chatApi'
import { useChannels } from '../../hooks/useChatQueries'
import { useDeleteChannel, useLeaveChannel, useUpdateChannelInteracted } from '../../hooks/useChatMutations'
import {
  type Channel,
  type Message,
  type MessageCountObject,
  type MessageObject
} from "../../utils/types";
import { useUserContext } from '../../../../utils/context'
import { WebsocketMessage } from '../../../../api/types';
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  setBadgeStatus_countMessages?: (count: number) => void
  header?: boolean
}

/**
 * Logic for channel operation (delete/create) and subscriptions
 */

/* ----------------------------- Component start ---------------------------- */
function ChannelLogicContent(props: Props): ReactElement | null {
  /* -------------------------- Non state data start -------------------------- */
  const { setBadgeStatus_countMessages } = props
  const log = new LogTool({ context: 'ChannelLogic', enable: true, logLevel: 'warn' })
  const queryClient = useQueryClient()
  const { user } = useUserContext()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState<boolean>(false)
  const [leaveChatDialogOpen, setLeaveChatDialogOpen] = useState<boolean>(false)
  const [createChannelMode, setCreateChannelMode] = useState<boolean>(false) // necessary for mobile
  // channel that is visited, helps to differentiate seen and unseen messages
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const dataSource = useRef<Channel[]>([])
  const [channels, setChannels] = useState<Channel[]>([]) // current channels
  const [selectedChannels, setSelectedChannels] = useState<Channel[]>([]) // channels that are selected for deletion
  const [selectedChannelLeave, setSelectedChannelLeave] = useState<Channel[]>([])
  const [newestMessage, setNewestMessage] = useState<Message | undefined>(undefined) // newest message in the channel
  // channel that is active and used for operations
  const [activeChannel, setActiveChannel] = useState<Channel | undefined>(undefined)
  const [visitedChannel, setVisitedChannel] = useState<Channel | undefined>(undefined)
  // channelIds with number of new Messages
  const [messages, setMessages] = useState<MessageObject>({}) // messages inside the active channel
  // new messages that have been received
  const [newMessages, setNewMessages] = useState<MessageObject>({})
  /* const [initNewMessages, setInitNewMessages] = useState<MessageObject>({}) */
  const [countMessages, setCountMessages] = useState<MessageCountObject>({}) // number of new received messages
  const { websocketMessage, websocketIsReady, websocketSend } = useWebsocket()
  const { t } = useTranslation(['chat', 'common'])

  /* ----------------------------- Data states end ---------------------------- */

  /* ---------------------------- React Query Hooks --------------------------- */
  // Disable chat queries for guest users - they don't have access to chat
  const isGuestUser = user?.isGuestUser ?? false
  const {
    data: channelsData,
    isLoading: isChannelsLoading,
    error: channelsError,
    refetch: refetchChannels
  } = useChannels({ expand: ['members', 'creator', 'messages'] }, !isGuestUser)

  const deleteChannelMutation = useDeleteChannel()
  const leaveChannelMutation = useLeaveChannel()
  const updateChannelInteractedMutation = useUpdateChannelInteracted()
  /* ------------------------- React Query Hooks End -------------------------- */

  /* ----------------------------------Effects start---------------------------------------- */
  // Update channels when data is fetched
  useEffect(() => {
    if (channelsData?.results) {
      const updatedChannels = channelsData.results.map(channel => {
        // Find the newest message for the channel
        const newestMessage = channel.messages[channel.messages.length - 1]
        // Add the newest message as a property to the channel
        return { ...channel, newestMessage }
      })

      // Update the state with the updated channels
      setChannels(channelsData.results)

      // Set the active channel, if channels are available
      if (updatedChannels.length > 0 && !activeChannel) {
        setActiveChannel(channelsData.results[0])
      }
    }
  }, [channelsData])

  useEffect(() => {
    // Don't fetch messages for guest users - they don't have access to chat
    if (!isGuestUser) {
      void (async () => {
        await fetchNewMessagesInChannels()
      })()
    }
  }, [user, isGuestUser])
  /* ---------------------------------------Effects end------------------------------------------- */
  /* -------------------------------WebSocket Functions start------------------------------------- */

  // useEffect(() => {
  //   if (socket) {
  //     socket.onmessage = async event => {
  //       handleSocketEvent(event)
  //     }
  //     socket.addEventListener('error', event => {
  //       log.error('socket error: ', event)
  //     })
  //   }
  // }, [socket, activeChannel])

  useEffect(() => {
    // Don't handle chat events for guest users - they don't have access to chat
    if (websocketIsReady && websocketMessage && !isGuestUser) {
      const msgType = websocketMessage.type
      if (msgType.startsWith('chat_')) {
        void (async () => {
          await handleSocketEvent(websocketMessage)
        })()
      }
    }
  }, [websocketMessage, isGuestUser])
  // TODO: udpate channels on delete/create in real time and update badge status
  /* -------------------------------WebSocket Functions end--------------------------------------- */

  async function handleSocketEvent(msg: WebsocketMessage): Promise<void> {
    if (isGuestUser) return
    switch (msg.type) {
      case 'chat_message':
        // const messageObject: MessageObject = JSON.parse(msg.data.message)
        const message: Message = createMessage(msg.data) // messageObject[Object.keys(messageObject)[0]][0]
        log.info('Message ->', message)
        setNewMessages(prevMessages => {
          const newMessages = { ...prevMessages }
          if (typeof message.channel === 'string') {
            // the backend only sends the id of the channel, the message was send in
            const channelId = getPKfromSelf(message.channel)
            if (newMessages[channelId]?.length > 0) {
              newMessages[channelId].push(message)
            } else {
              newMessages[channelId] = [message]
            }
          }
          return newMessages
        })
        setCountMessages((prevCount: MessageCountObject) => {
          const countMessages: MessageCountObject = { ...prevCount }
          if (
            typeof message.channel === 'string' &&
            typeof message.author !== 'string' &&
            user &&
            message.author?.key !== user.key &&
            activeChannel?.key !== message.channel
          ) {
            const channelId = message.channel
            countMessages[channelId] = countMessages[channelId] ? countMessages[channelId] + 1 : 1
          }
          return countMessages
        })
        setChannels(prevChannels => {
          const updatedChannels = prevChannels.map(channel => {
            if (channel.key === message.channel) {
              return { ...channel, newestMessage: message }
            }
            return channel
          })
          const sortedChannels: Channel[] = updatedChannels.sort((a: Channel, b: Channel): number => {
            const newestMessageA: Date | undefined = a.newestMessage?.createdAt
            const newestMessageB: Date | undefined = b.newestMessage?.createdAt
            if (!newestMessageA || !newestMessageB) return 0
            return (
              new Date(newestMessageB).getTime() -
              new Date(newestMessageA).getTime()
            )
          })
          return sortedChannels
        })
        break
      case 'channel_new':
        const newChannel: Channel = await fetchNewChannel(JSON.parse((msg.data as any).channel)) as Channel
        if (typeof newChannel.creator !== "string" && newChannel.creator.self !== user?.self) {
          setChannels((prevChannels: Channel[]) => [...prevChannels, newChannel])
        }
        break
      case 'channel_deleted':
        if (!isGuestUser) {
          await refetchChannels()
        }
        const deletedChannel = channels.find((channel: Channel) => channel.key === msg.data.channel)
        if (deletedChannel) {
          setChannels(prevChannels =>
            prevChannels.filter((channel: Channel) => channel.self !== deletedChannel.self)
          )
        }
        break
      case 'message_edit':
        if (msg.data.channel === activeChannel?.key) {
          await fetchMessagesForSocketEvent((msg.data as any).channel)
        }
        break
      default:
        break
    }
  }

  /* ------------------------- Utility functions start ------------------------ */

  async function fetchNewChannel(channel: number): Promise<Channel | null> {
    if (isGuestUser) return null
    try {
      const newChannel = await fetchChannel(channel, ['members', 'creator', 'messages'])
      log.info('Successfully fetched new channel: ', newChannel)
      return newChannel
    } catch (error) {
      log.error('Error: ', error)
      return null
    }
  }

  async function fetchMessagesForSocketEvent(channelId: number): Promise<void> {
    if (!activeChannel || isGuestUser) return
    try {
      const response = await fetchMessagesApi(channelId, { expand: ['self', 'author'] })
      if (response.results && response.results.length > 0) {
        const sortedMessages: Message[] = response.results.sort((a: Message, b: Message) => {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        })
        const newMessages = sortedMessages.filter((message: Message) => message.isNewMessage)
        const oldMessages = sortedMessages.filter((message: Message) => !message.isNewMessage)
        const newMessagesObject: MessageObject = {
          [activeChannel.self]: newMessages,
        }
        const oldMessagesObject: MessageObject = {
          [activeChannel.self]: oldMessages,
        }
        setNewMessages(newMessagesObject)
        setMessages(oldMessagesObject)
      } else {
        setMessages({ [activeChannel.self]: [] })
      }
    } catch (error) {
      log.error('Error fetching messages', error)
    }
  }

  async function fetchNewMessagesInChannels(): Promise<void> {
    if (!user || isGuestUser) return

    try {
      const response = await fetchChannelsApi({ expand: ['messages'] })
      const countObject: MessageCountObject = {}
      response.results.forEach(channel => {
        if (channel.messages.length > 0 && Array.isArray(channel.messages)) {
          channel.messages.forEach((message: Message) => {
            const messageAuthorSelf =
              typeof message.author === 'string'
                ? message.author
                : message.author?.self

            if (message.isNewMessage && messageAuthorSelf !== user.self) {
              countObject[channel.self] = countObject[channel.self]
                ? countObject[channel.self] + 1
                : 1
            }
          })
        }
      })
      setCountMessages(countObject)
    } catch (error) {
      log.error('Error: ', error)
    }
  }

  async function setChannelInteraction(): Promise<void> {
    if (activeChannel) {
      setVisitedChannel(activeChannel)
      try {
        await updateChannelInteractedMutation.mutateAsync(getPKfromSelf(activeChannel.self))
        log.info('Successfully set channel interaction')
      } catch (error) {
        log.error('Error: ', error)
      }
    }
  }

  async function handleChannelDelete(): Promise<void> {
    setDeleteMultipleModal(false)
    const results = await Promise.all(selectedChannels.map(channel => deleteChannel(channel)))
    log.debug('Results: ', results)
    if (results.includes(false)) {
      toast.error(t('common:feedback.error.errorDeletingChats'))
    } else {
      toast.success(t('common:feedback.success.successDeletingChats'))
    }
    setSelectedChannels([])
    setActiveChannel(undefined)
    await refetchChannels()
  }

  async function deleteChannel(channel: Channel): Promise<boolean> {
    try {
      await deleteChannelMutation.mutateAsync(getPKfromSelf(channel.self))
      log.info('Successfully deleted channel')
      return true
    } catch (error) {
      log.error('Error deleting channel', error)
      return false
    }
  }

  // WIP: handle channel leave for one channel
  async function handleLeaveChat(): Promise<void> {
    setLeaveChatDialogOpen(false)
    const channel: Channel = selectedChannelLeave[0]
    log.debug('Channel: ', channel)
    log.debug('Channel self: ', channel.self)
    log.debug('Active user: ', user?.self)
    try {
      await leaveChannelMutation.mutateAsync(getPKfromSelf(channel.self))
      toast.success(t('common:feedback.success.removeMemberSuccess'))
    } catch (error) {
      log.error('Error removing member', error)
      toast.error(t('common:feedback.error.removeMemberError'))
    }
    await refetchChannels()
    setSelectedChannels([])
  }

  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (): void => {
    setActiveChannel(undefined)
    if (setBadgeStatus_countMessages) {
      setBadgeStatus_countMessages(0)
    }
  }

  //allows chatview to be closed on mobile
  const handleMobileCloseChatView = (): void => {
    setActiveChannel(undefined)
    setCreateChannelMode(false)
  }

  const handleCreateChannelClick = (): void => {
    setActiveChannel(undefined)
    setCreateChannelMode(true)
  }

  const handleChannelClick = async (channel: Channel | undefined): Promise<void> => {
    setVisitedChannel(activeChannel)
    setCountMessages(prevCount => {
      const countMessages = { ...prevCount }
      if (channel && countMessages[channel?.self] > 0) {
        countMessages[channel?.self] = 0
      }
      return countMessages
    })
    setActiveChannel(channel)
    await setChannelInteraction()
  }

  /* ------------------------- Callback functions end ------------------------- */

  if (isChannelsLoading || !websocketIsReady) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  log.debug('open: ', deleteMultipleModal)
  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  // Create a wrapper function to match the expected signature in ChatContext
  const fetchChannelsWrapper = async (args: any): Promise<void> => {
    await refetchChannels()
  }

  return (
    <div>
      {channels && channels?.length >= 0 && !setBadgeStatus_countMessages && (
        <ChatContext.Provider
          value={{
            channels,
            setChannels,
            messages,
            setMessages,
            newMessages,
            setNewMessages,
            newestMessage,
            countMessages,
            activeUser: (user ?? ({} as User)),
            visitedChannel,
            activeChannel,
            setActiveChannel,
            fetchChannels: fetchChannelsWrapper,
            createChannelMode,
            handleMobileCloseChatView,
            handleCreateChannelClick,
            handleChannelClick,
            handleChannelDelete,
            handleClose,
            selectedChannelLeave,
            setSelectedChannelLeave,
            selectedChannels,
            setSelectedChannels,
            setDeleteMultipleModal,
            leaveChatDialogOpen,
            setLeaveChatDialogOpen,
          }}
        >
          <ChannelOverview />
        </ChatContext.Provider>
      )}
      <DeleteModal
        open={deleteMultipleModal}
        onDelete={handleChannelDelete}
        name={t('chat:feedback.info.doYouReallyWantToDeleteThisChats')}
        setOpen={setDeleteMultipleModal}
      />
      <Dialog open={leaveChatDialogOpen} onClose={() => setLeaveChatDialogOpen(false)}>
        <DialogTitle>{t('chat:feedback.info.doYouReallyWantToLeaveThisChat')}</DialogTitle>
        <DialogContent>
          <p>{`${t('chat:content.label.subject')}: ${selectedChannelLeave[0]?.name}`}</p>
          <p>{t('chat:feedback.info.youWillNotBeAbleToSeeTheMessages')}</p>
        </DialogContent>
        <DialogActions>
          <Button
            id="cancelLeaveChatButton"
            color="primary"
            variant="outlined"
            sx={{ float: 'left' }}
            onClick={() => {
              setLeaveChatDialogOpen(false)
              setSelectedChannels([])
            }}
          >
            {t('common:interaction.button.cancel')}
          </Button>
          <Button id="leaveChatButton" variant="contained" onClick={() => handleLeaveChat()}>
            {t('common:interaction.button.leave')}
          </Button>
        </DialogActions>
  </Dialog>
</div>
  )
}

export default function ChannelLogic(props: Props): ReactElement | null {
  if (props.header) {
    return null
  }

  return <ChannelLogicContent {...props} />
}
